/**
 * `time_tracking` — the accumulated duration, a start/stop control and the session log.
 *
 * The **value** is read-only through the cell (*"cannot be updated or cleared via the API. The
 * timer can only be started, stopped, and managed through the monday.com UI or automations"*
 * `[09§A.7]`), so the popover's buttons call the timer endpoints rather than writing a value;
 * the grid receives the new duration through the ordinary cell patch.
 *
 * **Not captured**: monday's own log popover never opened under three patient clicks
 * (`editors/time_tracking/tier2-capture.json`), so the *layout* here is our standard picker
 * chrome. What is captured is the settings dialog — *Time presentation ▸ Show seconds* — and
 * the session shape itself is monday's own `TimeTrackingValue.history[]` `[cvs§2]`.
 */
import {
  durationText,
  getColumnType,
  type TimeTrackingSettings,
  type TimeTrackingValue,
} from '@elizmonday/shared';
import { useState } from 'react';
import { CellPopover } from '../primitives';
import { requestCellAction } from '../cell-action';
import { defineColumnUi } from '../types';
import { sharedBuckets } from '../buckets';
import { TimeTrackingSettingsDialog } from './SettingsDialog';

const shared = getColumnType('time_tracking');

export const TIME_TRACKING_WIDTH = 260;

/** `1h 05m` for the grid; the full `HH:MM:SS` is monday's `text` and stays in the tooltip. */
export function shortDuration(seconds: number, showSeconds: boolean): string {
  if (seconds <= 0) return '';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  if (hours > 0) return `${String(hours)}h ${String(minutes).padStart(2, '0')}m`;
  if (minutes > 0) return showSeconds ? `${String(minutes)}m ${String(rest).padStart(2, '0')}s` : `${String(minutes)}m`;
  return `${String(rest)}s`;
}

/**
 * The panel behind the cell: the total, Start/Stop, and monday's **manual entry** — the fourth
 * verb `cells.timer.addSession`, which sets all four `manually_entered_*` flags (M2 tier-2
 * §5.7). Every one of them is a service call, not a cell write: *"the timer can only be
 * started, stopped and managed through the monday.com UI or automations"* `[09§A.7]`, so they
 * are raised on the cell-action channel and `table/cell-actions.ts` calls the procedure
 * (P-128).
 *
 * **Not captured** (as this folder's header already says): monday's own log popover never
 * opened under three patient clicks, so the two fields and their default — the **hour up to
 * now** — are **ASSUMED**.
 */
function localInputValue(at: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${String(at.getFullYear())}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

const LOG_DEFAULT_MINUTES = 60;

function TimerPanel({
  value,
  settings,
  columnId,
  itemId,
  onCancel,
}: {
  value: TimeTrackingValue | null;
  settings: TimeTrackingSettings;
  columnId: string;
  itemId: string;
  onCancel(): void;
}) {
  const now = new Date();
  const [from, setFrom] = useState(() =>
    localInputValue(new Date(now.getTime() - LOG_DEFAULT_MINUTES * 60_000)),
  );
  const [to, setTo] = useState(() => localInputValue(now));
  const startedAt = new Date(from);
  const endedAt = new Date(to);
  const valid =
    !Number.isNaN(startedAt.getTime()) && !Number.isNaN(endedAt.getTime()) && endedAt > startedAt;

  return (
    <CellPopover label="Time tracking" width={TIME_TRACKING_WIDTH} onDismiss={onCancel}>
      <div className="ez-timer-panel">
        <div className="ez-timer-panel__total" data-testid="timer-total">
          {durationText(value?.duration ?? 0) || '00:00:00'}
        </div>
        <button
          type="button"
          className="ez-picker__row ez-picker__row--centered"
          data-testid="timer-toggle"
          onClick={() =>
            requestCellAction(value?.running === true ? 'timer-stop' : 'timer-start', columnId, itemId)
          }
        >
          {value?.running === true ? 'Stop' : 'Start'}
        </button>
        <div className="ez-timer-panel__log">
          <input
            type="datetime-local"
            aria-label="Session start"
            data-testid="timer-from"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
          />
          <input
            type="datetime-local"
            aria-label="Session end"
            data-testid="timer-to"
            value={to}
            onChange={(event) => setTo(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
          />
        </div>
        <button
          type="button"
          className="ez-picker__row"
          data-testid="timer-log"
          disabled={!valid}
          onClick={() =>
            requestCellAction('timer-log', columnId, itemId, {
              session: { startedAt: startedAt.toISOString(), endedAt: endedAt.toISOString() },
            })
          }
        >
          Log time
        </button>
        <p className="ez-two-field__hint">
          {settings.showSeconds === false ? 'Seconds hidden by this column’s settings.' : ''}
        </p>
      </div>
    </CellPopover>
  );
}

export const timeTrackingColumnUi = defineColumnUi<TimeTrackingValue, TimeTrackingSettings>({
  type: 'time_tracking',
  icon: 'columnCreationLog',
  gallery: { group: 'numbers', keywords: ['time tracking', 'timer', 'duration', 'hours'] },
  /** P-022: the registry default. ASSUMED — no `width: null` column of this type is measured. */
  defaultWidth: 140,
  align: 'center',
  groupable: false,
  /** The editor is the timer and its log, not a value editor, so it opens on a read-only cell too. */
  opensWhenReadOnly: true,

  Cell: ({ value, settings }) => {
    if (value === null) return <span className="ez-cell__empty" aria-hidden="true" />;
    return (
      <span className="ez-timer" data-running={value.running === true ? 'true' : 'false'} title={durationText(value.duration)}>
        {value.running === true ? <span className="ez-timer__dot" aria-hidden="true" /> : null}
        {shortDuration(value.duration, settings.showSeconds !== false)}
      </span>
    );
  },

  Editor: ({ value, settings, column, item, onCancel }) => <TimerPanel
    value={value}
    settings={settings}
    columnId={String(column.id)}
    itemId={String(item.id)}
    onCancel={onCancel}
  />,

  SettingsDialog: TimeTrackingSettingsDialog,

  fromClipboard() {
    throw new Error('the time tracking column cannot be pasted into');
  },
  toClipboard(value, settings) {
    return shared.toText(value, settings) ?? '';
  },
  bucketsOf: sharedBuckets('time_tracking'),
});
